import React, { forwardRef } from 'react';

import styled, { css, keyframes } from 'styled-components';

import MegaMenuImageCard from 'components/Layout/Header/components/MegaMenuItem/components/MegaMenuImageCard';
import MegaMenuItemBody from 'components/Layout/Header/components/MegaMenuItem/components/MegaMenuItemBody/MegaMenuItemBody';

import { MenuColumnSubMenuItemI } from 'interfaces/content-stack/contentStack.types';

const fadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const MegaMenuWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const MegaMenuContentContainer = styled.div<{ $display: boolean; $index: number }>`
  display: none;
  opacity: 0;
  position: absolute;
  z-index: 1;
  padding-top: 0.75rem;
  transform: translate(-10rem);
  width: max-content;
  max-width: calc(100vw - 20rem);

  ${(props) =>
    props.$display &&
    css`
      display: flex;
      animation: ${fadeIn} 0.2s forwards;
    `}

  ${(props) =>
    props.$index > 1 &&
    `
    transform: translate(-20rem);
  `}

  ${(props) =>
    props.$index > 3 &&
    `
    transform: translate(-30rem);
  `}

  @media (max-width: ${(props) => props.theme.breakpoints.lg}) {
    width: initial;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.xl}) {
    max-width: calc(100vw - 12.5rem);
  }
`;

const MegaMenuContent = styled.div`
  background-color: ${(props) => props.theme.colors.white};
  padding: 1.5rem;
  overflow: hidden;
  box-shadow: 0 1.1875rem 0.875rem -0.0625rem rgba(0, 0, 0, 0.2);
  display: flex;
`;

interface MegaMenuData {
  submenu: MenuColumnSubMenuItemI[];
}

interface MegaMenuItemProps {
  children: React.ReactNode;
  index: number;
  data: MegaMenuData[] | undefined;
  isMenuOpen: boolean;
  onMouseLeave?: () => void;
  onKeyDown?: (event: React.KeyboardEvent) => void;
}

const MegaMenuItem = forwardRef<HTMLDivElement, MegaMenuItemProps>(
  ({ children, data, index, isMenuOpen, onMouseLeave, onKeyDown }, ref) => {
    if (!data) {
      return null;
    }

    const firstSubMenuItem = data[0].submenu && data[0].submenu[0];
    const remainingSubMenuItems = data?.slice(1);

    return (
      <MegaMenuWrapper onMouseLeave={onMouseLeave} ref={ref} data-testid='menu-item-test-id' onKeyDown={onKeyDown}>
        {children}
        <MegaMenuContentContainer
          $index={index}
          $display={isMenuOpen}
          data-testid='mega-menu-content-container-test-id'
        >
          <MegaMenuContent>
            {firstSubMenuItem && <MegaMenuImageCard data={firstSubMenuItem} />}
            {remainingSubMenuItems && <MegaMenuItemBody data={remainingSubMenuItems} />}
          </MegaMenuContent>
        </MegaMenuContentContainer>
      </MegaMenuWrapper>
    );
  }
);

export default MegaMenuItem;
